import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, Pause, Play, RefreshCw, Search, Trash2 } from 'lucide-react';
import { DatePicker } from '../components/date-picker';
import { PageHeader, Row, Select } from '../components/form';
import { Alert, Badge, Button, Card, Input, cn } from '../components/ui';
import { api } from '../lib/api';

interface CdrRow {
  id: string;
  callId: string;
  direction: 'inbound' | 'outbound' | 'internal';
  fromNumber: string | null;
  fromName: string | null;
  toNumber: string | null;
  did: string | null;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  durationSec: number | null;
  billsec: number | null;
  ringSec: number | null;
  disposition: string | null;
  hangupBy: string | null;
  trunkName: string | null;
  recordingId: string | null;
  recordingDuration: number | null;
  /** The outcome a person tagged the call with, separate from what the switch observed. */
  dispositionName: string | null;
  dispositionTone: string | null;
  notes: string | null;
}

interface CallDisposition {
  id: string;
  name: string;
  tone: 'neutral' | 'success' | 'warning' | 'destructive';
  isActive: boolean;
}

interface Page {
  data: CdrRow[];
  nextCursor: string | null;
}

const DISPOSITION_TONE: Record<string, 'success' | 'warning' | 'destructive' | 'muted'> = {
  answered: 'success',
  no_answer: 'warning',
  busy: 'warning',
  abandoned: 'warning',
  voicemail: 'muted',
  failed: 'destructive',
};

const DISPOSITION_LABEL: Record<string, string> = {
  answered: 'ANSWERED',
  no_answer: 'NO ANSWER',
  busy: 'BUSY',
  abandoned: 'ABANDONED',
  voicemail: 'VOICEMAIL',
  failed: 'FAILED',
};

/** Shared by the initial state and Clear, so a new filter cannot be added to one and not the other. */
const EMPTY_FILTERS = {
  number: '',
  direction: '',
  disposition: '',
  trunkId: '',
  from: '',
  to: '',
};

function clock(seconds: number | null): string {
  if (seconds === null) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

/**
 * Call detail records.
 *
 * Laid out the way PBX admins read a CDR: filters across the top, then one row per call with the
 * three durations that matter kept separate. Total, ring and talk answer different questions —
 * "did we pick up quickly" is ring, "how long did we speak" is talk — and collapsing them into
 * one number loses both.
 */
export function Cdr() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(filters);
  const [cursor, setCursor] = useState<string | null>(null);
  const [stack, setStack] = useState<string[]>([]);

  const query = new URLSearchParams({ limit: '25' });
  if (cursor) query.set('cursor', cursor);
  if (applied.number) query.set('number', applied.number);
  if (applied.direction) query.set('direction', applied.direction);
  if (applied.disposition) query.set('disposition', applied.disposition);
  if (applied.trunkId) query.set('trunkId', applied.trunkId);
  if (applied.from) query.set('from', applied.from);
  if (applied.to) query.set('to', new Date(`${applied.to}T23:59:59`).toISOString());

  const { data: dispositions = [] } = useQuery({
    queryKey: ['call-dispositions'],
    queryFn: () => api.get<CallDisposition[]>('/api/v1/call-dispositions'),
  });

  // Named rather than typed: a trunk id is a uuid, so the only usable filter is a list to pick
  // from. Disabled trunks stay in it — old calls that came in over one are still in the history.
  const { data: trunks = [] } = useQuery({
    queryKey: ['trunks'],
    queryFn: () => api.get<Array<{ id: string; name: string }>>('/api/v1/trunks'),
  });

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['cdr', query.toString()],
    queryFn: async () => {
      const response = await fetch(`/api/v1/cdr?${query.toString()}`, {
        credentials: 'same-origin',
      });
      return (await response.json()) as Page;
    },
  });

  const rows = data?.data ?? [];

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  // Position and length of whatever is playing, so the row can show a real seek bar. Duration
  // only becomes known once metadata loads, and only becomes SEEKABLE because the audio endpoint
  // answers range requests — without those the bar renders and does nothing.
  const [position, setPosition] = useState({ at: 0, of: 0 });

  const toggle = (row: CdrRow) => {
    const element = audioRef.current;
    if (!element || !row.recordingId) return;

    if (playing === row.id) {
      element.pause();
      setPlaying(null);
      return;
    }

    element.src = `/api/v1/recordings/${row.recordingId}/audio`;
    setPosition({ at: 0, of: row.recordingDuration ?? 0 });
    setPlaying(row.id);
    void element.play().catch(() => setPlaying(null));
  };

  const search = () => {
    setApplied(filters);
    setCursor(null);
    setStack([]);
  };

  const exportUrl = `/api/v1/cdr/export?days=30`;

  return (
    <div className="space-y-5">
      <PageHeader
        title="CDR"
        description="Every call this PBX has handled, with its recording where one exists."
      />

      <audio
        ref={audioRef}
        onEnded={() => setPlaying(null)}
        onTimeUpdate={(e) =>
          setPosition((p) => ({ ...p, at: (e.target as HTMLAudioElement).currentTime }))
        }
        onLoadedMetadata={(e) => {
          const el = e.target as HTMLAudioElement;
          // Infinity happens with a streamed container whose length is not yet known; the row's
          // own recorded duration is a better answer than a broken bar.
          setPosition({ at: 0, of: Number.isFinite(el.duration) ? el.duration : 0 });
        }}
        className="hidden"
      />

      <Card className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <Row label="From date">
            <DatePicker
              value={filters.from}
              onChange={(from) => setFilters((f) => ({ ...f, from }))}
              placeholder="Any date"
              aria-label="From date"
            />
          </Row>
          <Row label="To date">
            <DatePicker
              value={filters.to}
              onChange={(to) => setFilters((f) => ({ ...f, to }))}
              placeholder="Any date"
              aria-label="To date"
            />
          </Row>
          <Row label="Number" hint="Matches either side of the call.">
            <Input
              value={filters.number}
              onChange={(e) => setFilters((f) => ({ ...f, number: e.target.value }))}
              placeholder="1001"
            />
          </Row>
          <Row label="Direction">
            <Select
              value={filters.direction}
              onChange={(v) => setFilters((f) => ({ ...f, direction: v }))}
            >
              <option value="">Any</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
              <option value="internal">Internal</option>
            </Select>
          </Row>
          <Row label="Status">
            <Select
              value={filters.disposition}
              onChange={(v) => setFilters((f) => ({ ...f, disposition: v }))}
            >
              <option value="">Any</option>
              <option value="answered">Answered</option>
              <option value="no_answer">No answer</option>
              <option value="busy">Busy</option>
              <option value="abandoned">Abandoned</option>
              <option value="voicemail">Voicemail</option>
              <option value="failed">Failed</option>
            </Select>
          </Row>
          <Row label="Trunk">
            <Select
              value={filters.trunkId}
              onChange={(v) => setFilters((f) => ({ ...f, trunkId: v }))}
            >
              <option value="">Any</option>
              {trunks.map((trunk) => (
                <option key={trunk.id} value={trunk.id}>
                  {trunk.name}
                </option>
              ))}
            </Select>
          </Row>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={search}>
            <Search size={14} />
            Search
          </Button>
          <Button variant="secondary" onClick={() => void refetch()}>
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : undefined} />
            Refresh
          </Button>
          <a href={exportUrl} download>
            <Button variant="secondary">
              <Download size={14} />
              Download CDR
            </Button>
          </a>
          <Button
            variant="secondary"
            onClick={() => {
              setFilters(EMPTY_FILTERS);
              setApplied(EMPTY_FILTERS);
              setCursor(null);
              setStack([]);
            }}
          >
            Clear
          </Button>
        </div>
      </Card>

      <div className="overflow-x-auto rounded-[--radius] border border-border bg-card">
        <table className="w-full min-w-[64rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left">
              {[
                'Time',
                'Call from',
                'Call to',
                'Call duration',
                'Ring duration',
                'Talk duration',
                'Status',
                'Ended by',
                'Trunk',
                'DID',
                'Outcome',
                'Recording',
              ].map((header) => (
                <th
                  key={header}
                  className="whitespace-nowrap px-3 py-2.5 font-medium text-muted-foreground"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-3 py-12 text-center text-muted-foreground">
                  No calls match these filters.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="whitespace-nowrap px-3 py-2.5 tabular-nums">
                    {new Date(row.startedAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="font-mono">{row.fromNumber ?? '—'}</span>
                    {row.fromName && row.fromName !== row.fromNumber && (
                      <span className="ml-1.5 text-muted-foreground">{row.fromName}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono">{row.toNumber ?? row.did ?? '—'}</td>
                  <td className="px-3 py-2.5 tabular-nums">{clock(row.durationSec)}</td>
                  <td className="px-3 py-2.5 tabular-nums">{clock(row.ringSec)}</td>
                  <td className="px-3 py-2.5 tabular-nums">{clock(row.billsec)}</td>
                  <td className="px-3 py-2.5">
                    <Badge tone={DISPOSITION_TONE[row.disposition ?? ''] ?? 'muted'}>
                      {DISPOSITION_LABEL[row.disposition ?? ''] ?? row.disposition ?? '—'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">{row.hangupBy ?? '—'}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{row.trunkName ?? '—'}</td>
                  {/* The number the caller dialled, which is no longer in "Call to" now that that
                      column names whoever picked up. */}
                  <td className="px-3 py-2.5 font-mono text-muted-foreground">{row.did ?? '—'}</td>
                  <td className="px-3 py-2.5">
                    <Outcome row={row} dispositions={dispositions} onSaved={() => void refetch()} />
                  </td>
                  <td className="px-3 py-2.5">
                    {row.recordingId ? (
                      <div className="flex items-center gap-2">
                        {playing === row.id && (
                          <div className="flex min-w-40 items-center gap-2">
                            <input
                              type="range"
                              min={0}
                              max={position.of || row.recordingDuration || 0}
                              step={0.1}
                              value={position.at}
                              onChange={(e) => {
                                const el = audioRef.current;
                                if (!el) return;
                                el.currentTime = Number(e.target.value);
                                setPosition((p) => ({ ...p, at: Number(e.target.value) }));
                              }}
                              className="h-1 w-28 cursor-pointer accent-primary"
                              aria-label="Seek"
                            />
                            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                              {clock(Math.floor(position.at))}
                            </span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => toggle(row)}
                          className={cn(
                            'rounded-[--radius] p-1.5 text-muted-foreground',
                            'hover:bg-muted hover:text-foreground',
                          )}
                          aria-label={playing === row.id ? 'Pause recording' : 'Play recording'}
                          title={playing === row.id ? 'Pause' : 'Play'}
                        >
                          {playing === row.id ? (
                            <Pause size={15} className="text-primary" />
                          ) : (
                            <Play size={15} />
                          )}
                        </button>
                        <a
                          href={`/api/v1/recordings/${row.recordingId}/audio?download=1`}
                          download
                          className="rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          aria-label="Download recording"
                          title="Download"
                        >
                          <Download size={15} />
                        </a>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          disabled={stack.length === 0}
          onClick={() => {
            const previous = [...stack];
            const back = previous.pop() ?? null;
            setStack(previous);
            setCursor(back);
          }}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          disabled={!data?.nextCursor}
          onClick={() => {
            if (!data?.nextCursor) return;
            setStack((s) => [...s, cursor ?? '']);
            setCursor(data.nextCursor);
          }}
        >
          Next
        </Button>
        <span className="text-sm text-muted-foreground">
          Showing {rows.length} call{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      {rows.length > 0 && rows.every((r) => !r.recordingId) && (
        <Alert tone="warning">
          None of these calls were recorded. Recording is off by default — turn it on per extension
          under Extensions, or from Call Features → Recording.
        </Alert>
      )}
    </div>
  );
}

/**
 * Tag a call with a business outcome and a note.
 *
 * Two different things share the word "disposition" in telephony, and keeping them apart is the
 * point of this column: `disposition` is what the switch observed (answered, busy, no answer),
 * while this is what a person decided the call was about. Only the second one can answer "how
 * many of last week's calls were complaints".
 */
function Outcome({
  row,
  dispositions,
  onSaved,
}: {
  row: CdrRow;
  dispositions: CallDisposition[];
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(row.notes ?? '');

  const save = useMutation({
    mutationFn: (patch: { callDispositionId?: string | null; notes?: string | null }) =>
      api.patch(`/api/v1/cdr/${encodeURIComponent(row.callId)}`, patch),
    onSuccess: () => {
      setOpen(false);
      onSaved();
    },
  });

  const active = dispositions.filter((d) => d.isActive);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-left"
        title={row.notes ?? 'Add an outcome'}
      >
        {row.dispositionName ? (
          <Badge tone={(row.dispositionTone as 'success' | 'warning' | 'destructive') ?? 'muted'}>
            {row.dispositionName}
          </Badge>
        ) : (
          <span className="text-muted-foreground underline-offset-4 hover:underline">Tag…</span>
        )}
        {row.notes && <span className="ml-1 text-muted-foreground">✎</span>}
      </button>
    );
  }

  return (
    <div className="min-w-56 space-y-2">
      <Select
        value={
          row.dispositionName ? (active.find((d) => d.name === row.dispositionName)?.id ?? '') : ''
        }
        onChange={(id) => save.mutate({ callDispositionId: id || null })}
      >
        <option value="">No outcome</option>
        {active.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </Select>
      <Input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Note"
        onBlur={() => {
          if (notes !== (row.notes ?? '')) save.mutate({ notes });
        }}
      />
      <button
        type="button"
        className="text-xs text-muted-foreground underline-offset-4 hover:underline"
        onClick={() => setOpen(false)}
      >
        Close
      </button>
      {active.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No outcomes defined yet. Add some under Call Features.
        </p>
      )}
    </div>
  );
}
